/**
 *
 *  Phone Number form
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 10/09/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAppState } from "../../../state";
import { useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";
import PhoneCodeInput from "../../PhoneCodeInput/PhoneCodeInput";
import DialingCodeSelectorPopup from "../../PhoneCodeInput/DialingCodeSelectorPopup/DialingCodeSelectorPopup";

// Hooks
import useCountryName from "../../../hooks/useCountryName/useCountryName";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";

// Utils
import {
  formatPhoneNumber,
  getCountryInfo,
  getCountryInfoByPhoneCode,
  isAndroidDevice,
  isIosDevice,
  isMac,
} from "../../../utils";

export default function SetPhoneNumber({ onNext }) {
  const { checkPhoneNumber, verifyPhoneNumber, error, setError, isFetching } =
    useAppState();
  const {
    setUserPhoneCode,
    userPhoneCode,
    setUserPhoneNumber,
    userPhoneNumber,
    authVendor,
    isAppleSignIn,
  } = useSignUpState();

  const [canSend, setCanSend] = React.useState(false);
  const [phoneCode, setPhoneCode] = React.useState("+1");
  const [countryFlag, setCountryFlag] = React.useState("🇺🇸");
  const [showChooseCodePopup, setShowChooseCodePopup] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [latlng, setLatlng] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [hasError, setHasError] = useState(false);

  const [, countryShortName, lookupAddress] = useCountryName(latlng);

  const phoneNumberRef = React.useRef();
  const phoneCodeInputRef = React.useRef();

  useEffect(() => {
    if (phoneNumberRef.current) {
      phoneNumberRef.current.focus();
    }
  }, []);

  useEffect(() => {
    let latLng;

    if (navigator.geolocation /* && !userPhoneCode*/) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          latLng = `${pos.coords.latitude},${pos.coords.longitude}`;
          setLatlng(latLng);

          lookupAddress(latLng);
        },
        (err) => {
          console.warn(`ERROR(${err.code}): ${err.message}`);
          setLatlng(`${34.098907},${-118.327759}`);

          // lookupAddress(latlng);
        }
      );
    }
  }, [userPhoneCode, lookupAddress]);

  useEffect(() => {
    if (userPhoneCode && userPhoneNumber) {
      let [countryShortName, countryFlag, countryFlagImage] =
        getCountryInfoByPhoneCode(userPhoneCode);

      setPhoneCode(userPhoneCode);
      setPhoneNumber(userPhoneNumber);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);

      if (validatePhoneNumber(userPhoneNumber)) {
        setCanSend(true);
      }
    }
  }, [userPhoneCode, userPhoneNumber]);

  useEffect(() => {
    if (countryShortName) {
      let [phoneCode, countryFlag, countryFlagImage] =
        getCountryInfo(countryShortName);

      setPhoneCode(phoneCode);
      setUserPhoneCode(phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);
    }
  }, [setUserPhoneCode, countryShortName]);

  const handlePhoneCodeSelect = (countryInfo) => {
    if (countryInfo) {
      setPhoneCode(countryInfo.phoneCode);
      setUserPhoneCode(countryInfo.phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice
          ? countryInfo.emoji
          : countryInfo.image
      );
      setSelectedCountry(countryInfo.code);
      setShowChooseCodePopup(false);
      // setHasError(false);
    }
  };

  const handleDialingCodeSelectorPopupClose = () => {
    setShowChooseCodePopup(false);
  };

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value;

    value = formatPhoneNumber(value);

    setPhoneNumber(value);

    if (validatePhoneNumber(value)) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }

    setHasError(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setHasError(false);
    setError(null);

    if (phoneCode && phoneNumber) {
      setUserPhoneNumber(phoneNumber);

      // For migration
      if ((isAppleSignIn && authVendor === undefined) || authVendor) {
        checkPhoneNumber(phoneCode, phoneNumber)
          .then((res) => {
            return verifyPhoneNumber({
              phoneCode: res.migrationData
                ? res.migrationData.phoneCode
                : phoneCode,
              phoneNumber: res.migrationData
                ? res.migrationData.phoneNumber
                : phoneNumber,
              authVendor:
                isAppleSignIn && authVendor === undefined
                  ? "apple"
                  : authVendor,
            }).then((res) => ({ isLogin: true }));
          })
          .then((res) => {
            onNext(
              isAppleSignIn && authVendor === undefined ? "apple" : authVendor,
              {},
              res.isLogin
            );
          })
          .catch(() => {
            setHasError(true);
          });
      } else {
        verifyPhoneNumber({
          phoneCode,
          phoneNumber,
          authVendor:
            isAppleSignIn && authVendor === undefined ? "apple" : authVendor,
        })
          .then(() => {
            onNext();
          })
          .catch(() => {
            setHasError(true);
          });
      }
    }
  };

  return (
    <div className="tbk-relative">
      <div className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
        Your Phone Number
      </div>
      <p className="tbk-text-main tbk-mb-4 tbk-text-grey-main">
        Please, provide us your mobile phone number. It is required for future
        bookings and account security.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="tbk-mb-2 tbk-flex">
          <PhoneCodeInput
            ref={phoneCodeInputRef}
            className="tbk-mr-1"
            countryFlag={countryFlag}
            countryShortName={countryShortName}
            phoneCode={phoneCode}
            onClick={() =>
              showChooseCodePopup ? null : setShowChooseCodePopup(true)
            }
          />
          <input
            ref={phoneNumberRef}
            type="tel"
            placeholder="Phone number"
            className="tbk-input-field tbk-text-title-medium tbk-h-7 tbk-grow tbk-pr-2"
            style={{ marginBottom: 0 }}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            onKeyUp={handlePhoneNumberChange}
          />
        </div>
        {hasError ? (
          <div className="tbk-text-main tbk--mt-1 tbk-mb-2 tbk-text-center tbk-text-coral">
            {error ? error.message : "Unknown error has occurred"}
          </div>
        ) : null}
        <Button
          type="submit"
          className="tbk-mb-3 tbk-w-full"
          disabled={!canSend || isFetching}
          endIcon={canSend ? <CheckNiceIcon /> : <ArrowRightIcon />}
        >
          Accept
        </Button>
      </form>
      <DialingCodeSelectorPopup
        phoneCodeInputRef={phoneCodeInputRef}
        show={showChooseCodePopup}
        selectedCountry={selectedCountry}
        onSelect={handlePhoneCodeSelect}
        onClose={handleDialingCodeSelectorPopupClose}
      />
    </div>
  );
}

function validatePhoneNumber(phone) {
  return phone && phone.replace(/\s/g, "").length >= 9;
}
